import React, { useState, useRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import Sidebar from './Sidebar';
import NavigationBar from './NavigationBar';
import ChatInterface, { ChatInterfaceRef } from './ChatInterface';
import FlashMessages from './FlashMessages';
import { useRouter } from '../router';

interface MainLayoutProps {
  onSignOut: () => void;
  conversationId: string;
  initialMessage?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ onSignOut, conversationId, initialMessage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const chatRef = useRef<ChatInterfaceRef>(null);
  const { navigateToHome, navigateToConversation } = useRouter();

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNewChat = async () => {
    // Navigate to home route (which will generate a new conversation ID)
    navigateToHome();
    
    // Reset chat state by calling the clear function
    if (chatRef.current) {
      await chatRef.current.clearMessages();
    }
  };

  const handleSelectConversation = (selectedConversationId: string) => {
    // Navigate to the specific conversation
    navigateToConversation(selectedConversationId);
    
    // Close the sidebar on mobile
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Navigation Bar - Always at top, full width */}
      <NavigationBar
        onSignOut={onSignOut}
        onMenuToggle={handleMenuToggle}
        onNewChat={handleNewChat}
      />

      {/* Flash Messages - Positioned below navigation */}
      <FlashMessages />

      {/* Content area with sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, pt: '64px' }}>
        {/* Sidebar */}
      <Sidebar
        open={isMobile ? sidebarOpen : true}
        onClose={handleCloseSidebar}
        onNewChat={handleNewChat}
        onSelectConversation={handleSelectConversation}
      />        {/* Main Content Area */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            ml: isMobile ? 0 : '280px', // Account for sidebar on desktop
            height: 'calc(100vh - 64px)', // Account for AppBar height
            overflow: 'hidden', // Prevent scrolling at this level
          }}
        >
          {/* Chat Interface */}
          <ChatInterface ref={chatRef} initialConversationId={conversationId} initialMessage={initialMessage} />
        </Box>

        {/* Overlay for mobile sidebar */}
        {isMobile && sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: '64px', // Start below AppBar
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: theme.zIndex.drawer - 1,
            }}
            onClick={handleCloseSidebar}
          />
        )}
      </Box>
    </Box>
  );
};

export default MainLayout;
